/* eslint-disable import/prefer-default-export */
import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessageType,
    ValidationMessage,
    SettingState,
    Setting,
    TibboDataType,
} from '@appblocks/codegeneration/types';
import BaseNode from '@appblocks/codegeneration/Nodes/BaseNode';
import NodePort from '@appblocks/codegeneration/Nodes/NodePort';
import NodeParameter from '@appblocks/codegeneration/Nodes/NodeParameter';


export const getCodeBlocks = (feature: BaseNode): CodeBlock[] => {
    const blocks: CodeBlock[] = [];
    const project: ProjectConfiguration = feature.project;
    const SERIAL_PORT_FEATURE_ID = feature.getSettingValue('SERIAL_PORT');
    const serialPortFeature = project.getFeature(SERIAL_PORT_FEATURE_ID);
    if (!SERIAL_PORT_FEATURE_ID || !serialPortFeature) {
        return [];
    }
    const serialPortName = serialPortFeature.getSettingValue('NAME');
    const name = serialPortName;
    const PORT = serialPortFeature.getSettingValue('PORT');

    blocks.push({
        location: 'VARIABLE_DECLARATIONS',
        blockType: BlockType.CODE,
        id: 'uart_msg_def',
        template: `
struct uart_msg {
    struct k_work work;
    char buf[128];
};`,
    });

    blocks.push({
        location: 'VARIABLE_DECLARATIONS',
        blockType: BlockType.CODE,
        id: `uart_${name}_msg_def`,
        template: `
#define MSG_SIZE_${name} 128

struct uart_msg ${name}_message;

/* queue to store up to 10 messages (aligned to 4-byte boundary) */
K_MSGQ_DEFINE(uart_msgq_${name}, MSG_SIZE_${name}, 10, 4);


static char rx_buf_${name}[MSG_SIZE_${name}];
static int rx_buf_pos_${name};

`,
    });

    blocks.push({
        location: 'boot',
        blockType: BlockType.CODE,
        template: `
int ret_${name} = uart_irq_callback_user_data_set(uart_dev_${name}, serial_${name}_cb, NULL);

if (ret_${name} < 0) {
    if (ret_${name} == -ENOTSUP) {
        printk("Interrupt-driven UART API support not enabled\\n");
    } else if (ret_${name} == -ENOSYS) {
        printk("UART device does not support interrupt-driven API\\n");
    } else {
        printk("Error setting UART callback: %d\\n", ret_${name});
    }
}
uart_irq_rx_enable(uart_dev_${name});

k_work_init(&${name}_message.work, ${name}_handler);
`,
    });

    blocks.push({
        location: 'FUNCTION_DEFINITIONS',
        blockType: BlockType.CODE,
        id: `serial_${name}_cb`,
        template: `
void serial_${name}_cb(const struct device *dev, void *user_data)
{
    uint8_t c;

    //if (!uart_irq_update(uart_dev_${name})) {
    //    return;
    //}

    //if (!uart_irq_rx_ready(uart_dev_${name})) {
    //    return;
    //}
    int i = 0;
    /* read until FIFO empty */
    while (uart_fifo_read(uart_dev_${name}, &c, 1) == 1) {
        
            rx_buf_${name}[rx_buf_pos_${name}++] = c;
        i++;
        /* else: characters beyond buffer size are dropped */
    }

    rx_buf_${name}[rx_buf_pos_${name}] = '\\0';

    /* if queue is full, message is silently dropped */
    k_msgq_put(&uart_msgq_${name}, &rx_buf_${name}, K_NO_WAIT);

    /* reset the buffer (it was copied to the msgq) */
    rx_buf_pos_${name} = 0;
}

void ${name}_handler(struct k_work *item)
{
    struct uart_msg *the_message =
        CONTAINER_OF(item, struct uart_msg, work);
    string data = the_message->buf;
    string complete_data = data;
    /****${name}_ser_data_available****/
}

void ${name}_thread(int unused1, int unused2, int unused3) {
  char tx_buf[MSG_SIZE_${name}];
  LOG_INF("%s","${name} thread start");
  while (1) {
      /* indefinitely wait for input from the user */
      if (k_msgq_get(&uart_msgq_${name}, &tx_buf, K_FOREVER) == 0) {
          string serial_data = tx_buf;

          strcpy(${name}_message.buf, tx_buf);
          k_work_submit(&${name}_message.work);
      }

      k_sleep(K_MSEC(100));
  }
}

`,
    });

    blocks.push({
        location: 'THREAD_DEFINITIONS',
        blockType: BlockType.CODE,
        id: `${name}_thread`,
        template: `
K_THREAD_DEFINE(${name}_tid, 2048,
                ${name}_thread, NULL, NULL, NULL,
                K_LOWEST_APPLICATION_THREAD_PRIO, 0, 0);
`,
    });

    const links = project.getNodeLinks(feature, 'data_available');
    if (links.length > 0) {
        for (let i = 0; i < links.length; i++) {
            const link = links[i];
            blocks.push({
                location: `${serialPortName}_ser_data_available`,
                blockType: BlockType.CODE,
                id: link.getSourceId(),
                template: `/****${link.getSourceId()}****/`,
            });
        }
    }
    return blocks;
};
